import React, { useState, useEffect } from 'react';
import { 
  FileText, 
  Plus, 
  Search,
  MoreVertical,
  Trash2,
  CheckCircle,
  Clock,
  AlertCircle,
  DollarSign
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import { useSettings } from '../context/SettingsContext';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusColors = {
  unpaid: 'bg-red-50 text-red-700 border-red-200',
  partial: 'bg-amber-50 text-amber-700 border-amber-200',
  paid: 'bg-emerald-50 text-emerald-700 border-emerald-200'
};

const statusIcons = {
  unpaid: <AlertCircle className="h-3 w-3" />,
  partial: <Clock className="h-3 w-3" />,
  paid: <CheckCircle className="h-3 w-3" />
};

const PurchaseBills = () => {
  const { currencySymbol } = useSettings();
  const [bills, setBills] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [formData, setFormData] = useState({});
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    fetchBills();
    fetchSuppliers();
    fetchOrders();
  }, []);

  const fetchBills = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/procurement/bills`);
      setBills(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/procurement/suppliers`);
      setSuppliers(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchOrders = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/procurement/purchase-orders`);
      setOrders(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleSave = async () => {
    try {
      await axios.post(`${API_URL}/api/procurement/bills`, formData);
      toast.success('Bill recorded');
      setShowDialog(false);
      setFormData({});
      fetchBills();
    } catch (err) {
      toast.error('Failed to create bill');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this bill?')) {
      try {
        await axios.delete(`${API_URL}/api/procurement/bills/${id}`);
        toast.success('Bill deleted');
        fetchBills();
      } catch (err) {
        toast.error('Failed to delete');
      }
    }
  };

  const openNew = () => {
    setFormData({ amount: 0, tax: 0 });
    setShowDialog(true);
  };

  const filteredBills = bills.filter(b => 
    filterStatus === 'all' || b.status === filterStatus
  );

  const stats = {
    total: bills.length,
    unpaid: bills.filter(b => b.status === 'unpaid').length,
    totalPayable: bills.filter(b => b.status !== 'paid').reduce((sum, b) => sum + (b.total - b.paid_amount), 0),
    totalAmount: bills.reduce((sum, b) => sum + b.total, 0)
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Purchase Bills</h1>
          <p className="text-muted-foreground">Record and track bills from suppliers.</p>
        </div>
        <Button onClick={openNew} className="gap-2"><Plus className="h-4 w-4" /> Add Bill</Button>
      </div>

      <div className="grid grid-cols-4 gap-4">
        <Card><CardContent className="pt-6"><div className="text-2xl font-bold">{stats.total}</div><div className="text-sm text-muted-foreground">Total Bills</div></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="text-2xl font-bold text-red-600">{stats.unpaid}</div><div className="text-sm text-muted-foreground">Unpaid</div></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="text-2xl font-bold text-amber-600">{currencySymbol} {stats.totalPayable.toLocaleString()}</div><div className="text-sm text-muted-foreground">Total Payable</div></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="text-2xl font-bold">{currencySymbol} {stats.totalAmount.toLocaleString()}</div><div className="text-sm text-muted-foreground">Total Billed</div></CardContent></Card>
      </div>

      <div className="flex items-center gap-4">
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-40"><SelectValue placeholder="Filter" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="unpaid">Unpaid</SelectItem>
            <SelectItem value="partial">Partial</SelectItem>
            <SelectItem value="paid">Paid</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Bill #</TableHead>
              <TableHead>Supplier</TableHead>
              <TableHead>PO #</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Due Date</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead className="text-right">Paid</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-[50px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredBills.map((bill) => (
              <TableRow key={bill.id}>
                <TableCell className="font-mono font-medium">{bill.bill_number}</TableCell>
                <TableCell>{bill.supplier_name}</TableCell>
                <TableCell className="font-mono text-muted-foreground">{bill.po_number || '-'}</TableCell>
                <TableCell className="text-muted-foreground">{format(new Date(bill.date), 'MMM d, yyyy')}</TableCell>
                <TableCell className="text-muted-foreground">{bill.due_date ? format(new Date(bill.due_date), 'MMM d, yyyy') : '-'}</TableCell>
                <TableCell className="text-right font-mono font-bold">{currencySymbol} {bill.total?.toLocaleString()}</TableCell>
                <TableCell className="text-right font-mono text-emerald-600">{currencySymbol} {bill.paid_amount?.toLocaleString()}</TableCell>
                <TableCell>
                  <Badge className={`${statusColors[bill.status]} gap-1`}>
                    {statusIcons[bill.status]} {bill.status}
                  </Badge>
                </TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8"><MoreVertical className="h-4 w-4" /></Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem className="text-destructive" onClick={() => handleDelete(bill.id)}>
                        <Trash2 className="h-4 w-4 mr-2" /> Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
            {filteredBills.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} className="h-32 text-center">
                  <FileText className="h-8 w-8 mx-auto text-muted-foreground/30 mb-2" />
                  <p className="text-muted-foreground">No bills found</p>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Create Bill Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Record Purchase Bill</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Supplier *</Label>
              <Select value={formData.supplier_id || ''} onValueChange={v => setFormData({...formData, supplier_id: v})}>
                <SelectTrigger><SelectValue placeholder="Select supplier" /></SelectTrigger>
                <SelectContent>{suppliers.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Link to PO (Optional)</Label>
              <Select value={formData.po_id || 'none'} onValueChange={v => setFormData({...formData, po_id: v === 'none' ? '' : v})}>
                <SelectTrigger><SelectValue placeholder="Select PO" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {orders.filter(o => o.supplier_id === formData.supplier_id).map(o => (
                    <SelectItem key={o.id} value={o.id}>{o.po_number}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Bill Number</Label>
                <Input value={formData.bill_number || ''} onChange={e => setFormData({...formData, bill_number: e.target.value})} placeholder="INV-001" />
              </div>
              <div className="space-y-2">
                <Label>Date</Label>
                <Input type="date" value={formData.date || ''} onChange={e => setFormData({...formData, date: e.target.value})} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Due Date</Label>
              <Input type="date" value={formData.due_date || ''} onChange={e => setFormData({...formData, due_date: e.target.value})} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Amount *</Label>
                <Input type="number" step="0.01" value={formData.amount || ''} onChange={e => setFormData({...formData, amount: parseFloat(e.target.value) || 0})} />
              </div>
              <div className="space-y-2">
                <Label>Tax</Label>
                <Input type="number" step="0.01" value={formData.tax || ''} onChange={e => setFormData({...formData, tax: parseFloat(e.target.value) || 0})} />
              </div>
            </div>
            <div className="bg-muted/50 rounded-lg p-4">
              <div className="flex justify-between font-bold text-lg">
                <span>Total</span>
                <span className="font-mono">{currencySymbol} {((formData.amount || 0) + (formData.tax || 0)).toFixed(2)}</span>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea value={formData.notes || ''} onChange={e => setFormData({...formData, notes: e.target.value})} rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSave}>Record Bill</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PurchaseBills;
